import { createEntityAdapter, createSlice, createSelector } from '@reduxjs/toolkit'
import { filter, get, sortBy, orderBy } from 'lodash'

import ActionTypes from '@constants/action_types.js'
import { getSortParams } from '@features/sorting/index.ts'
import { getFilterParams } from '@features/filtering/index.ts'
import { getSortableFieldsForTable } from '@selectors/data/field_selectors.ts'
import TimetoastTemporal from '@base/timetoast_temporal.js'

const rowsAdapter = createEntityAdapter()

export const rowsSlice = createSlice({
  name: 'rows',
  initialState: rowsAdapter.getInitialState(),
  reducers: {
    rowRemove: rowsAdapter.removeOne,
  },
  extraReducers: builder => {
    builder.addCase(ActionTypes.RECEIVE_ENTITIES, (state, action) => {
      const { payload } = action
      if (payload.hasOwnProperty('rows')) {
        rowsAdapter.upsertMany(state, payload.rows)
      }
    })
  },
})

// Selectors

export const rowsSelectors = rowsAdapter.getSelectors(state => state.data.rows)

export const getRowsForTable = createSelector(
  (_, props) => props.tableId,
  rowsSelectors.selectAll,
  function (tableId, allRows) {
    return filter(allRows, row => {
      const rowTableId = get(row, ['attributes', 'table_id'])
      return rowTableId.toString() === tableId.toString()
    })
  }
)

export const getRowsById = createSelector([getRowsForTable], rows => sortBy(rows, row => row.id))

// Resolves the row's value for a given field into something that is sortable
const resolveSortableValue = (field, value) => {
  let sortableValue = ''
  if (field.attributes.variant_type === 'TimelineField') {
    // turn timeline value into something that's sortable
    // the value is an object that looks like: { s: '2021-01-01', e: '2021-01-02' } where either s or e could be null
    const valueToParse = value ?? { s: null, e: null }
    const chosenValue = valueToParse.s ?? valueToParse.e

    // TODO: Decide what to return when have no dates
    // For now, we'll always return the max safe integer, so they always appear last
    try {
      if (chosenValue === null) {
        sortableValue = TimetoastTemporal.MS_MAX
      } else {
        const timetoastTemporal = TimetoastTemporal.parse(chosenValue)
        if (timetoastTemporal) {
          sortableValue = timetoastTemporal.getTime()
        } else {
          sortableValue = TimetoastTemporal.MS_MAX
        }
      }
    } catch (error) {
      sortableValue = TimetoastTemporal.MS_MAX
    }
  } else {
    // stringish values
    sortableValue = value ?? ''
  }
  return sortableValue
}

export const getSortedRows = createSelector(
  [getSortableFieldsForTable, getRowsById, getSortParams],
  (sortableField, rows, params) => {
    if (params.length > 0) {
      const iteratee = ({ fieldId }) => {
        return row => {
          const id = `field_${fieldId}`
          const field = sortableField.find(field => field.id === fieldId)
          const sortableFieldValue = resolveSortableValue(field, row?.attributes?.values[id])
          return sortableFieldValue
        }
      }

      const iterateeDirection = ({ direction }) => {
        return direction
      }

      const result = orderBy(rows, params.map(iteratee), params.map(iterateeDirection))
      return result
    }

    return rows
  }
)

export const getFilteredAndSortedRows = createSelector([getSortedRows, getFilterParams], (rows, filterParams) => {
  if (filterParams.length > 0) {
    let updatedRows = []

    for (let i = 0; i < filterParams.length; i++) {
      const rowsToFilter = i === 0 ? rows : updatedRows
      const param = filterParams[i]

      const { fieldId, value, operator } = param
      const filterValue = value.toLowerCase()

      if (filterValue === '' || fieldId === '' || operator === '') {
        updatedRows = rowsToFilter
      }

      switch (operator) {
        case 'Contains': {
          updatedRows = rowsToFilter.filter(row => {
            const id = `field_${fieldId}`
            const fieldValue = row?.attributes?.values[id] ?? ''

            return fieldValue.toLowerCase().includes(filterValue)
          })
          break
        }
        case 'Excludes': {
          updatedRows = rowsToFilter.filter(row => {
            const id = `field_${fieldId}`
            const fieldValue = row?.attributes?.values[id] ?? ''

            if (filterValue === '') {
              return true
            }

            return !fieldValue.toLowerCase().includes(filterValue)
          })
          break
        }
        case 'Exactly Matches': {
          updatedRows = rowsToFilter.filter(row => {
            const id = `field_${fieldId}`
            const fieldValue = row?.attributes?.values[id] ?? ''

            if (filterValue === '') {
              return true
            }

            return fieldValue === filterValue
          })
          break
        }
      }
    }

    return updatedRows
  }

  return rows
})

export default rowsSlice.reducer
